import React, { useState, useEffect } from 'react';
import { FiX, FiMail, FiCheckCircle } from 'react-icons/fi';

import { useApplyToService } from '../../api/services';
import { useOpenConversation } from '../../api/conversations';
import { useMessageQueueStore } from '../../hooks/useMessageQueue';
import { Button } from '../ui/Button';
import { RichTextEditor } from '../ui/RichTextEditor';

export interface ApplyServiceData {
  id?: number;
  title?: string;
  price?: string;
  description?: string;
  creator?: {
    id?: number;
    full_name?: string;
    username?: string;
  };
}

interface ApplyServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: ApplyServiceData | null;
  serviceId: string | number;
  user?: { email?: string; fullName?: string; username?: string } | null;
  isAuthenticated?: boolean;
}

export const ApplyServiceModal: React.FC<ApplyServiceModalProps> = ({
  isOpen,
  onClose,
  service,
  serviceId,
  user,
  isAuthenticated,
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const { mutate: applyToService, isPending } = useApplyToService();
  const openConversationMutation = useOpenConversation();

  // Sync email when user changes or modal opens
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    } else if (!isAuthenticated) {
      setEmail('');
    }
  }, [user, isOpen, isAuthenticated]);

  // Re-build the pre-filled message whenever the service changes
  useEffect(() => {
    if (!service || !isOpen) return;
    const providerName =
      service.creator?.full_name || service.creator?.username || '[Provider Name]';
    const title = service.title || '[Service Name]';
    const userEmail = user?.email || '[Your email]';

    setMessage(
      `Hi ${providerName},\n\nI'm interested in your service "${title}" on Musicade.\n\nAbout Me\n[Brief introduction — who you are and what you're looking for]\n\nProject Details\n[Describe your event, project, or specific requirements]\n\nTimeline\n[Specify when you need the service and any deadlines]\n\nBudget\n[Provide your budget range or request pricing details]\n\nContact Details\nEmail: ${userEmail}`,
    );
    setIsSuccess(false);
  }, [service, isOpen]);

  if (!isOpen || !service) return null;

  const handleSubmit = () => {
    if (!email) return;

    applyToService(
      {
        serviceId: service.id || serviceId,
        data: {
          email,
          sender_role: 'inquirer',
          message_html: /<[a-z][\s\S]*>/i.test(message) ? message : message.replace(/\n/g, '<br>'),
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        },
      },
      {
        onSuccess: async () => {
          setIsSuccess(true);
          const providerId = service.creator?.id || (service as any).user;
          const sId = service.id || serviceId;
          if (providerId && sId) {
            try {
              const conv = await openConversationMutation.mutateAsync({
                recipient_id: providerId,
                context_type: 'service',
                context_id: sId.toString(),
              });
              if (conv?.conversation_uuid && message.trim()) {
                useMessageQueueStore.getState().enqueue({
                  conversationUuid: conv.conversation_uuid,
                  body: message.trim(),
                });
              }
            } catch (err) {
              console.error('Failed to auto-start chat for inquiry:', err);
            }
          }
        },
      },
    );
  };

  const handleModalClose = () => {
    setIsSuccess(false);
    onClose();
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
        <div className="w-full sm:max-w-lg bg-[#0c0c0c] border border-white/10 rounded-t-3xl sm:rounded-[32px] overflow-hidden shadow-2xl">
          <div className="flex justify-end px-5 pt-5 sm:p-6 sm:pb-0">
            <button
              onClick={handleModalClose}
              className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
            >
              <FiX size={22} />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center px-6 pb-12 sm:px-10 sm:pb-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-5 sm:mb-6">
              <FiCheckCircle className="text-green-500 w-10 h-10 sm:w-12 sm:h-12" />
            </div>
            <h2 className="text-white text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Message Sent</h2>
            <p className="text-zinc-400 text-base sm:text-lg mb-6 sm:mb-8 max-w-xs">
              Your message has been sent to the service provider
            </p>
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl px-5 py-3.5 sm:px-6 sm:py-4">
              <p className="text-zinc-500 text-xs sm:text-sm">
                You'll be notified once the provider responds to your message. You can also track
                and continue this conversation directly from your messages inbox.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-2xl bg-[#0c0c0c] border border-white/10 rounded-t-3xl sm:rounded-[32px] overflow-hidden shadow-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 sm:px-8 sm:pt-8 sm:pb-4">
          <h3 className="text-white text-lg sm:text-xl font-bold">Contact Service Provider</h3>
          <button
            onClick={onClose}
            className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="px-5 pb-5 sm:px-8 sm:pb-8 flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          {/* Email Input */}
          <div className="space-y-1.5 sm:space-y-2">
            <label className="text-zinc-400 text-[11px] sm:text-xs font-semibold uppercase tracking-wider">
              Your Email Address *
            </label>
            <div className="relative group">
              <div className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-blue-400 transition-colors">
                <FiMail size={16} />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your Email address"
                className="w-full h-12 sm:h-14 bg-zinc-900/40 border border-white/10 rounded-xl sm:rounded-2xl pl-11 sm:pl-12 pr-4 text-sm sm:text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
            </div>
          </div>

          {/* Rich Text Editor */}
          <RichTextEditor
            value={message}
            onChange={setMessage}
            placeholder="Tell the service provider about your project..."
            minHeight="220px"
          />
        </div>

        {/* Footer */}
        <div className="px-5 py-4 sm:px-8 sm:py-6 border-t border-white/5 bg-zinc-900/20 flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={isPending || !email.trim() || !message.trim()}
            className="h-11 px-6 sm:h-12 sm:px-8 rounded-full bg-white text-black hover:bg-zinc-200 font-bold text-xs sm:text-sm tracking-tight shadow-lg shadow-white/5 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
          >
            {isPending ? 'Sending...' : 'Send message'}
          </Button>
        </div>
      </div>
    </div>
  );
};
